import React, { useState } from 'react';
import { 
  BarChart3, Building2, ChevronLeft, DollarSign, FileText, 
  LayoutDashboard, PieChart, TrendingUp as TrendUpIcon, TrendingUp,
  TrendingDown, Users, Wrench, Clock, ArrowRight, Activity, BookOpen, FileBarChart, Briefcase
} from 'lucide-react';
import { Property, Tenant, Invoice, Transaction, MaintenanceRequest, Employee, SalaryPayment } from '../types';

import PropertyReports from './Reports/PropertyReports';
import TenantReports from './Reports/TenantReports';
import MaintenanceReports from './Reports/MaintenanceReports';
import FinancialReports from './Reports/FinancialReports';
import ReportTable, { Column } from './ReportTable';

interface ReportsDashboardProps {
  properties: Property[];
  tenants: Tenant[];
  invoices: Invoice[];
  transactions: Transaction[];
  maintenance: MaintenanceRequest[];
  employees: Employee[];
  salaries: SalaryPayment[];
}

type MainTab = 'indicators' | 'reports';
type ReportSection = 'all' | 'properties' | 'tenants' | 'maintenance' | 'financial' | 'hr';

const ReportsDashboard: React.FC<ReportsDashboardProps> = ({ 
  properties, tenants, invoices, transactions, maintenance, employees, salaries 
}) => {
  const [mainTab, setMainTab] = useState<MainTab>('indicators');
  const [activeReport, setActiveReport] = useState<string | null>(null);
  const [selectedSubReport, setSelectedSubReport] = useState<string | undefined>(undefined);
  const [activeSection, setActiveSection] = useState<ReportSection>('all');

  // KPI Calculations
  const totalCollected = invoices.filter(inv => inv.status === 'Paid').reduce((sum, inv) => sum + Number(inv.amount), 0);
  const totalPending = invoices.filter(inv => inv.status === 'Pending').reduce((sum, inv) => sum + Number(inv.amount), 0);
  const totalExpenses = transactions.filter(exp => exp.type === 'Expense').reduce((sum, exp) => sum + Number(exp.amount), 0);
  const otherIncome = transactions.filter(exp => exp.type === 'Income').reduce((sum, exp) => sum + Number(exp.amount), 0);
  const totalRevenue = totalCollected + otherIncome;
  const netProfit = totalRevenue - totalExpenses;

  const totalUnits = properties.reduce((sum, p) => sum + (p.units?.length || 0), 0);
  const occupiedUnits = properties.reduce((sum, p) => sum + (p.units?.filter(u => u.tenantName).length || 0), 0);
  const occupancyRate = totalUnits > 0 ? Math.round((occupiedUnits / totalUnits) * 100) : 0;

  const reportCards = [
    { id: 'properties', sub: 'operations', title: 'تقرير عمليات العقارات', category: 'properties', icon: Building2, desc: 'تحليل الإيرادات والمصروفات لكل عقار' },
    { id: 'properties', sub: 'detailed', title: 'تقرير العقارات التفصيلي', category: 'properties', icon: FileText, desc: 'بيانات العقارات ونسبة النفقات الربحية' },
    { id: 'properties', sub: 'vacant', title: 'تقرير الوحدات الشاغرة', category: 'properties', icon: Activity, desc: 'حصر الوحدات الشاغرة ومدد الشغور' },
    
    { id: 'tenants', sub: 'brief', title: 'سجل المستأجرين المختصر', category: 'tenants', icon: Users, desc: 'قائمة المستأجرين مع تواريخ العقود' },
    { id: 'tenants', sub: 'renewals', title: 'تقرير تجديد العقود', category: 'tenants', icon: Clock, desc: 'العقود التي قاربت على الانتهاء' },
    { id: 'tenants', sub: 'detailed', title: 'تقرير المستأجرين المفصل', category: 'tenants', icon: FileText, desc: 'التاريخ الإيجاري الكامل للمستأجرين' },
    
    { id: 'maintenance', sub: 'requests', title: 'سجل تذاكر الصيانة', category: 'maintenance', icon: Wrench, desc: 'تفاصيل الأعطال والتكاليف الفنية' },
    { id: 'maintenance', sub: 'performance', title: 'تقرير سرعة الإنجاز', category: 'maintenance', icon: Clock, desc: 'قياس كفاءة الشركات في إنهاء الطلبات' },
    { id: 'maintenance', sub: 'companies', title: 'كفاءة شركات الصيانة', category: 'maintenance', icon: Briefcase, desc: 'تحليل تكاليف وطلبات مزودي الخدمة' },
    { id: 'maintenance', sub: 'contracts', title: 'تقرير عقود الصيانة', category: 'maintenance', icon: FileText, desc: 'رصد المبالغ وقيم العقود الفعالة' },
    
    { id: 'financial', sub: 'monthly', title: 'العوائد الإيجارية الشهرية', category: 'financial', icon: TrendUpIcon, desc: 'توزيع الإيرادات على مدار العام' },
    { id: 'financial', sub: 'unpaid', title: 'تقرير المديونيات والمعلقات', category: 'financial', icon: DollarSign, desc: 'حصر المبالغ التي لم تسدد بعد' },
    { id: 'financial', sub: 'pnl', title: 'بيان الربح والخسارة', category: 'financial', icon: PieChart, desc: 'الميزانية العامة والأداء السنوي' },
    { id: 'financial', sub: 'unit_yield', title: 'تقرير عوائد الوحدات', category: 'financial', icon: BarChart3, desc: 'قياس ربحية كل شقة/محل على حدة' },
    { id: 'financial', sub: 'expenses', title: 'تقرير الإيرادات والمصروفات التفصيلي', category: 'financial', icon: FileBarChart, desc: 'تحليل تفصيلي لكافة الإيرادات والمصروفات ونفقات التشغيل والرواتب' },

    { id: 'hr', sub: 'payroll', title: 'سجل رواتب الموظفين', category: 'hr', icon: Users, desc: 'كشف الرواتب المصروفة وحالة الدفع' },

    { id: 'financial', sub: 'ledger', title: 'دفتر اليومية وحركات الخزينة المجمعة', category: 'financial', icon: BookOpen, desc: 'سجل شامل لكل العمليات المالية الصادرة والواردة' },
  ];

  const filteredReports = activeSection === 'all' 
    ? reportCards 
    : reportCards.filter(r => r.category === activeSection);

  const handleOpenReport = (id: string, sub?: string) => {
      setActiveReport(id);
      setSelectedSubReport(sub);
  };

  const renderActiveReport = () => {
    switch (activeReport) {
      case 'properties':
        return <PropertyReports properties={properties} invoices={invoices} transactions={transactions} initialReport={selectedSubReport} />;
      case 'tenants':
        return <TenantReports tenants={tenants} properties={properties} initialReport={selectedSubReport} />;
      case 'maintenance':
        return <MaintenanceReports maintenance={maintenance} initialReport={selectedSubReport} />;
      case 'financial':
        return <FinancialReports properties={properties} invoices={invoices} transactions={transactions} salaries={salaries} maintenance={maintenance} initialReport={selectedSubReport} />;
      case 'hr':
        const payrollData = salaries.map(s => ({
            employeeName: s.employeeName,
            civilId: s.civilId || '-',
            amount: s.amount,
            period: s.period,
            paymentDate: s.paymentDate ? String(s.paymentDate).split('T')[0] : '-',
            status: s.status === 'Paid' ? 'مصروف' : 'معلق'
        }));
        const payrollColumns: Column[] = [
            { header: 'إسم الموظف', accessor: 'employeeName' },
            { header: 'الرقم المدني', accessor: 'civilId' },
            { header: 'الفترة (الشهر)', accessor: 'period' },
            { header: 'تاريخ الصرف', accessor: 'paymentDate' },
            { header: 'قيمة الراتب', accessor: 'amount', render: val => `${Number(val).toLocaleString()} د.ك` },
            { header: 'حالة الصرف', accessor: 'status' }
        ];
        return <ReportTable title="سجل رواتب الموظفين" columns={payrollColumns} data={payrollData} />;
      default:
        return null;
    }
  };

  return (
    <div className={`space-y-8 animate-in fade-in duration-500 ${activeReport ? '' : 'min-h-[calc(100vh-120px)] flex flex-col'}`}>
      {/* 1. Main Toggle Tabs (Hide when viewing a specific report) */}
      {!activeReport && (
        <div className="shrink-0 flex items-center justify-between pb-4 border-b border-slate-200">
           <div className="flex bg-slate-100 p-1.5 rounded-2xl shadow-inner overflow-x-auto hide-scrollbar max-w-[full]">
              <button
                  onClick={() => setMainTab('indicators')}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl font-black text-sm whitespace-nowrap transition-all ${
                      mainTab === 'indicators' 
                      ? 'bg-white text-fly-600 shadow-xl' 
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
              >
                  <LayoutDashboard className="w-5 h-5" />
                  لوحة المؤشرات
              </button>
              <button
                  onClick={() => setMainTab('reports')}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl font-black text-sm whitespace-nowrap transition-all ${
                      mainTab === 'reports' 
                      ? 'bg-white text-fly-600 shadow-xl' 
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
              >
                  <FileBarChart className="w-5 h-5" />
                  التقارير التفصيلية
              </button>
           </div>

           <div className="hidden md:flex flex-col items-end">
               <h2 className="text-2xl font-black text-slate-800">نظام إدارة وتحليل البيانات</h2>
               <p className="text-sm text-slate-400 font-bold">تحديث حي: {new Date().toLocaleDateString('ar-KW')}</p>
           </div>
        </div>
      )}

      <div className={`flex-1 ${activeReport ? 'min-h-[calc(100vh-150px)]' : 'overflow-hidden flex flex-col min-h-0'}`}>
        {mainTab === 'indicators' && !activeReport ? (
          <div className="flex-1 overflow-y-auto custom-scrollbar space-y-8 pr-2 pb-10">
              {/* KPIs Section */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-xl shadow-emerald-600/5 transition-all">
                    <div className="flex items-center gap-4 mb-4">
                        <div className="bg-emerald-50 p-4 rounded-2xl">
                            <TrendingUp className="text-emerald-600 w-6 h-6" />
                        </div>
                        <div>
                            <p className="text-xs font-black text-slate-400 uppercase tracking-widest">إجمالي المحصل</p>
                            <p className="text-2xl font-black text-slate-800">{totalRevenue.toLocaleString()} د.ك</p>
                        </div>
                    </div>
                    <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                        <div className="bg-emerald-500 h-full rounded-full" style={{ width: '100%' }}></div>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-xl shadow-orange-600/5 transition-all">
                    <div className="flex items-center gap-4 mb-4">
                        <div className="bg-orange-50 p-4 rounded-2xl">
                            <DollarSign className="text-orange-600 w-6 h-6" />
                        </div>
                        <div>
                            <p className="text-xs font-black text-slate-400 uppercase tracking-widest">مستحقات معلقة</p>
                            <p className="text-2xl font-black text-slate-800">{totalPending.toLocaleString()} د.ك</p>
                        </div>
                    </div>
                    <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                        <div className="bg-orange-500 h-full rounded-full" style={{ width: '40%' }}></div>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-xl shadow-rose-600/5 transition-all">
                    <div className="flex items-center gap-4 mb-4">
                        <div className="bg-rose-50 p-4 rounded-2xl">
                            <TrendingDown className="text-rose-600 w-6 h-6" />
                        </div>
                        <div>
                            <p className="text-xs font-black text-slate-400 uppercase tracking-widest">إجمالي المصروفات</p>
                            <p className="text-2xl font-black text-slate-800">{totalExpenses.toLocaleString()} د.ك</p>
                        </div>
                    </div>
                    <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                        <div className="bg-rose-500 h-full rounded-full" style={{ width: '60%' }}></div>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-xl shadow-fly-600/5 transition-all">
                    <div className="flex items-center gap-4 mb-4">
                        <div className="bg-fly-50 p-4 rounded-2xl">
                            <Activity className="text-fly-600 w-6 h-6" />
                        </div>
                        <div>
                            <p className="text-xs font-black text-slate-400 uppercase tracking-widest">معدل الإشغال</p>
                            <p className="text-2xl font-black text-slate-800">{occupancyRate}%</p>
                        </div>
                    </div>
                    <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                        <div className="bg-fly-500 h-full rounded-full" style={{ width: `${occupancyRate}%` }}></div>
                    </div>
                </div>
              </div>

              {/* Charts Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 pb-10">
                  <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-lg">
                      <div className="flex items-center gap-2 mb-8">
                        <PieChart className="w-5 h-5 text-fly-600" />
                        <h3 className="text-lg font-bold text-slate-800">توزيع حالة الوحدات</h3>
                      </div>
                      <div className="flex items-center justify-center p-10">
                          <div className="relative w-40 h-40">
                             <svg className="w-full h-full transform -rotate-90">
                                <circle cx="80" cy="80" r="70" stroke="currentColor" strokeWidth="15" fill="transparent" className="text-slate-100" />
                                <circle cx="80" cy="80" r="70" stroke="currentColor" strokeWidth="15" fill="transparent" 
                                    strokeDasharray={439.6} strokeDashoffset={439.6 - (439.6 * occupancyRate) / 100} strokeLinecap="round" className="text-fly-600 transition-all duration-1000" />
                             </svg>
                             <div className="absolute inset-0 flex flex-col items-center justify-center">
                                 <span className="text-3xl font-black text-slate-800">{occupancyRate}%</span>
                             </div>
                          </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                          <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 flex justify-between">
                                <span className="font-bold text-slate-500">شاغر</span>
                                <span className="font-black text-slate-800">{totalUnits - occupiedUnits}</span>
                          </div>
                          <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 flex justify-between">
                                <span className="font-bold text-slate-500">مؤجر</span>
                                <span className="font-black text-slate-800">{occupiedUnits}</span>
                          </div>
                      </div>
                  </div>

                  <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-lg">
                      <div className="flex items-center gap-2 mb-8">
                        <BarChart3 className="w-5 h-5 text-fly-600" />
                        <h3 className="text-lg font-bold text-slate-800">أعلى العقارات إيراداً</h3>
                      </div>
                      <div className="space-y-6">
                            {properties.slice(0, 4).map(property => {
                                const rev = invoices.filter(inv => String(inv.propertyId) === String(property.id) && inv.status === 'Paid').reduce((sum, inv) => sum + Number(inv.amount), 0);
                                const maxRev = Math.max(...properties.map(p => invoices.filter(inv => String(inv.propertyId) === String(p.id) && inv.status === 'Paid').reduce((s, i) => s + Number(i.amount), 0)), 1);
                                const perc = (rev / maxRev) * 100;

                                return (
                                    <div key={property.id}>
                                        <div className="flex justify-between mb-2">
                                            <span className="text-sm font-bold text-slate-700">{property.name}</span>
                                            <span className="text-sm font-black text-fly-600">{rev.toLocaleString()} د.ك</span>
                                        </div>
                                        <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                                            <div className="bg-fly-600 h-full rounded-full" style={{ width: `${perc}%` }}></div>
                                        </div>
                                    </div>
                                );
                            })}
                      </div>
                  </div>
              </div>
          </div>
        ) : (
          <div className="flex-1 min-h-0 flex flex-col">
            {!activeReport ? (
              <div className="flex-1 min-h-0 flex flex-col space-y-6">
                <div className="flex items-center gap-2 overflow-x-auto pb-2 hide-scrollbar shrink-0">
                    {(['all', 'properties', 'tenants', 'maintenance', 'financial', 'hr'] as ReportSection[]).map(section => (
                        <button
                            key={section}
                            onClick={() => setActiveSection(section)}
                            className={`px-6 py-2.5 rounded-2xl text-sm font-black transition-all whitespace-nowrap ${
                                activeSection === section 
                                ? 'bg-slate-900 text-white shadow-xl shadow-slate-900/20' 
                                : 'bg-white text-slate-500 border border-slate-200 hover:border-slate-300'
                            }`}
                        >
                            {section === 'all' ? 'جميع التصنيفات' : 
                             section === 'properties' ? 'العقارات' :
                             section === 'tenants' ? 'المستأجرين' :
                             section === 'maintenance' ? 'الصيانة' :
                             section === 'financial' ? 'التحليلات المالية' : 'الموظفين'}
                        </button>
                    ))}
                </div>

                <div className="flex-1 overflow-y-auto custom-scrollbar pb-10">
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6 pr-2">
                        {filteredReports.map(report => (
                            <button
                                key={`${report.id}-${report.sub}`}
                                onClick={() => handleOpenReport(report.id, report.sub)}
                                className="bg-white p-7 rounded-3xl border border-slate-200 hover:border-fly-400 hover:shadow-2xl hover:shadow-fly-100 transition-all flex flex-col text-right group relative overflow-hidden"
                            >
                                <div className="absolute top-0 right-0 w-32 h-32 bg-fly-50 rounded-full -translate-y-16 translate-x-16 group-hover:scale-150 transition-transform duration-700 opacity-50"></div>
                                <div className="relative z-10">
                                    <div className="bg-slate-50 p-4 rounded-2xl w-fit mb-6 group-hover:bg-fly-600 group-hover:text-white transition-all transform group-hover:rotate-6">
                                        <report.icon className="w-6 h-6" />
                                    </div>
                                    <h4 className="text-xl font-black text-slate-800 mb-2">{report.title}</h4>
                                    <p className="text-sm text-slate-500 leading-relaxed font-medium">{report.desc}</p>
                                    <div className="mt-8 flex items-center gap-2 text-fly-600 font-black text-xs group-hover:gap-4 transition-all">
                                        <span>استعراض التقرير التفصيلي</span>
                                        <ArrowRight className="w-4 h-4 -rotate-180" />
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col h-full">
                <div className="shrink-0 mb-6 flex items-center justify-between">
                    <button 
                      onClick={() => { setActiveReport(null); setSelectedSubReport(undefined); }}
                      className="flex items-center gap-2 px-6 py-2.5 bg-slate-900 text-white rounded-xl hover:bg-slate-800 font-black text-sm shadow-xl shadow-slate-900/20 transition-all active:scale-95"
                    >
                      <ChevronLeft className="w-4 h-4 rotate-180" />
                      العودة لقائمة التقارير
                    </button>
                </div>
                <div className="flex-1 min-h-0 bg-white rounded-3xl border border-slate-100 p-4">
                   {renderActiveReport()}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsDashboard;
